/**
 * <barkwoo-announcement-bar> — rotating announcement bar for
 * sections/barkwoo-announcement-bar.liquid (barkwoo-d3b.40).
 *
 * - Shows one [data-bw-announcement-slide] at a time (the rest carry `hidden`).
 * - Auto-rotates every `data-interval` seconds when `data-autoplay="true"`.
 * - Pauses while the pointer is over the bar or keyboard focus is inside it,
 *   and resumes when both leave (unless the visitor pressed Pause). Pressing
 *   Play overrides the focus hold.
 * - Never auto-rotates under prefers-reduced-motion: reduce; the toggle then
 *   starts in the "Play" state so a visitor can opt in.
 * - The visible Pause/Play toggle satisfies WCAG 2.2.2 (auto-updating content
 *   that lasts more than 5s needs a mechanism to pause it).
 * - Prev/next move one message and keep the current play state.
 * - Theme editor: selecting a block shows it and holds rotation until deselected.
 * - Emits `barkwoo:announcement:change` on document with { index }.
 */
class BarkwooAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.onPointerEnter = () => this.setHold('hover', true);
    this.onPointerLeave = () => this.setHold('hover', false);
    // Keyboard focus holds rotation; a mouse click that leaves focus on a
    // button does not (the hover hold already covers the pointer).
    this.onFocusIn = (event) => {
      if (event.target.matches(':focus-visible')) this.setHold('focus', true);
    };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.setHold('focus', false);
    };
    this.onClick = this.onClick.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-bw-announcement-slide]'));
    this.toggle = this.querySelector('[data-bw-announcement-toggle]');
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.intervalMs = Math.max(3, Number(this.dataset.interval) || 6) * 1000;
    this.holds = new Set();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // "playing" is the visitor-facing state the toggle shows; the timer also
    // stops for transient holds (hover, focus, editor selection).
    this.playing = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;

    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('click', this.onClick);
    this.reducedMotion.addEventListener('change', this.onMotionChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.setAttribute('data-ready', 'true');
    this.render();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('click', this.onClick);
    if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  onClick(event) {
    const control = event.target.closest('[data-bw-announcement-action]');
    if (!control || !this.contains(control)) return;
    const action = control.getAttribute('data-bw-announcement-action');
    if (action === 'toggle') {
      this.playing = !this.playing;
      // Pressing Play is an explicit request to rotate, so it overrides the
      // keyboard-focus hold (WAI-ARIA APG carousel pattern).
      if (this.playing) this.holds.delete('focus');
      this.render();
    } else if (action === 'next') {
      this.show(this.index + 1);
    } else if (action === 'previous') {
      this.show(this.index - 1);
    }
  }

  onMotionChange() {
    if (this.reducedMotion.matches) this.playing = false;
    this.render();
  }

  onBlockSelect(event) {
    const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
    if (slideIndex === -1) return;
    this.holds.add('editor');
    this.show(slideIndex);
  }

  onBlockDeselect() {
    this.setHold('editor', false);
  }

  setHold(reason, active) {
    if (active) this.holds.add(reason);
    else this.holds.delete(reason);
    this.syncTimer();
  }

  show(nextIndex) {
    const count = this.slides.length;
    this.index = ((nextIndex % count) + count) % count;
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== this.index;
    });
    document.dispatchEvent(
      new CustomEvent('barkwoo:announcement:change', { detail: { index: this.index } })
    );
    this.syncTimer();
  }

  render() {
    if (this.toggle) {
      const label = this.playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.setAttribute('data-state', this.playing ? 'playing' : 'paused');
    }
    this.syncTimer();
  }

  syncTimer() {
    this.stopTimer();
    if (!this.playing || this.holds.size > 0) return;
    this.timer = window.setTimeout(() => this.show(this.index + 1), this.intervalMs);
  }

  stopTimer() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('barkwoo-announcement-bar')) {
  customElements.define('barkwoo-announcement-bar', BarkwooAnnouncementBar);
}
